import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import VehicleMpProvider from "@/core/data-provider/data-mp/vehicle/vehicle-mp-provider";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import { QueryKey } from "@/domains/transportWarehouse/constants/queryKey";
import toast from "@/domains/toasts/helpers/toast";
import { fetchVehiclesRelated } from "@/domains/transportWarehouse/helpers/fetchVehiclesRelated";
import useTransportWarehouseCacheData from "@/domains/transportWarehouse/hooks/useTransportWarehouseCacheData";
import TransportWarehouseTransportPinTransportModal from "@/domains/transportWarehouse/components/transport/TransportWarehouseTransportPinTransportModal";

type Props = {
  onClose: () => void;
  slotIndex: number;
};

export default function TransportWarehouseTransportPinTransportModalContainer({
  onClose,
  slotIndex,
}: Props) {
  const queryClient = useQueryClient();
  const [activeVehicleId, setActiveVehicleId] = useState<null | number>(null);
  const [isPutting, setIsPutting] = useState(false);
  const { transportWarehouseInfo } = useTransportWarehouseCacheData();

  const { data, isFetching, isError } = useQuery({
    queryKey: [QueryKey.VehiclesRelated],
    queryFn: fetchVehiclesRelated,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: true,
  });

  const putIntoGarage = useCallback(
    async (vehicleId: number) => {
      setIsPutting(true);
      const response = await VehicleMpProvider.putIntoGarage({
        garageId: transportWarehouseInfo.id,
        index: slotIndex,
        vehicleId,
      });
      if (response instanceof RpcMpProviderSuccessResponse) {
        onClose();
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [QueryKey.VehiclesRelated] }),
          queryClient.invalidateQueries({ queryKey: [QueryKey.TransportWarehouseInfo] }),
        ]);
      } else {
        toast.error(response.message);
      }
      setIsPutting(false);
    },
    [transportWarehouseInfo, slotIndex, onClose, queryClient],
  );

  if (isError) {
    return "error";
  }

  return (
    <TransportWarehouseTransportPinTransportModal
      onClose={onClose}
      onPin={putIntoGarage}
      isPinning={isPutting}
      isLoading={isFetching}
      activeVehicleId={activeVehicleId}
      onChangeActiveVehicleId={setActiveVehicleId}
      vehicles={data}
    />
  );
}
